import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FileText, CheckCircle, XCircle, User, Plus, TrendingUp,
  Sparkles, Clock, AlertCircle, Package, ShoppingCart,
  BarChart3, Activity, GripVertical, Star, ClipboardList, ShoppingBag,
  Calendar, Boxes, AlertTriangle, ArrowUpRight, ArrowDownRight,
  Zap, Target, Users, RefreshCw, LayoutDashboard, Building2,
  Trophy, Award, Medal, ChevronRight, TrendingDown
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
  AreaChart, Area, RadialBarChart, RadialBar
} from 'recharts';
import API_BASE_URL from "../config";

// ─── CUSTOM TOOLTIP ──────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 text-white rounded-xl p-3 shadow-xl text-xs border border-gray-700">
      <p className="text-gray-400 mb-2 font-semibold">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 mb-1">
          <div className="w-2 h-2 rounded-sm" style={{ background: p.color }} />
          <span className="text-gray-300">{p.name}:</span>
          <span className="font-bold">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtShort = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

const STATUS_MAP = {
  0: { label: 'Draft',    color: '#6B7280', bg: 'bg-gray-100',   text: 'text-gray-700'   },
  1: { label: 'Pending',  color: '#F59E0B', bg: 'bg-yellow-100', text: 'text-yellow-800' },
  2: { label: 'Approved', color: '#10B981', bg: 'bg-green-100',  text: 'text-green-800'  },
  3: { label: 'Rejected', color: '#EF4444', bg: 'bg-red-100',    text: 'text-red-800'    },
};

const DEPT_COLORS = [
  '#6366f1','#f59e0b','#10b981','#ec4899','#f97316',
  '#06b6d4','#8b5cf6','#14b8a6','#84cc16','#a78bfa',
];

const getInitials = (name = '') =>
  name.split(' ').map(n => n[0] || '').join('').slice(0, 2).toUpperCase() || '?';

const filterByPeriod = (arr, period, dateKey = 'creationDate') => {
  if (period === 'all') return arr;
  const days = { month: 30, week: 7, '3days': 3, day: 1 }[period] ?? 30;
  const cutoff = Date.now() - days * 864e5;
  return arr.filter(x => new Date(x[dateKey]) >= cutoff);
};

const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status] ?? STATUS_MAP[0];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
};

const Stars = ({ rating }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }, (_, i) => (
      <Star key={i} size={12} className={i < Math.round(rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'} />
    ))}
  </div>
);

const TimeFilterBtns = ({ value, onChange, options }) => (
  <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl">
    {options.map(({ key, label }) => (
      <button
        key={key}
        onClick={() => onChange(key)}
        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
          value === key ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

const PERIOD_OPTS = [
  { key: 'month', label: 'Month' },
  { key: 'week',  label: 'Week'  },
  { key: '3days', label: '3 Days'},
  { key: 'all',   label: 'All'   },
];

// ─── KPI CARD ────────────────────────────────────────────────────────────────
const KPICard = ({ label, value, suffix = '', icon: Icon, gradient, textColor, sub, trend }) => (
  <div className="bg-white rounded-2xl p-5 shadow-md border border-gray-100 hover:shadow-xl transition-all duration-300 group cursor-default">
    <div className="flex items-center justify-between mb-3">
      <div className={`p-2.5 bg-gradient-to-br ${gradient} rounded-xl shadow group-hover:scale-110 transition-transform`}>
        <Icon className="text-white" size={18} />
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full ${trend >= 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
          {trend >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
          {Math.abs(trend)}%
        </div>
      )}
    </div>
    <p className={`text-3xl font-black ${textColor} mb-0.5`}>
      {value}<span className="text-lg ml-1 font-bold opacity-60">{suffix}</span>
    </p>
    <p className="text-xs text-gray-500 font-semibold">{label}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

// ─── RANK CARD (for Top Requestors / Departments) ────────────────────────────
const RankCard = ({ rank, name, count, label, color, sub }) => {
  const medals = [
    { icon: Trophy, bg: 'from-yellow-400 to-amber-500' },
    { icon: Award,  bg: 'from-gray-300 to-gray-400'    },
    { icon: Medal,  bg: 'from-orange-400 to-orange-500' },
  ];
  const m = medals[rank - 1];
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5">
      <div className="flex items-center gap-3 mb-3">
        {m ? (
          <div className={`w-9 h-9 bg-gradient-to-br ${m.bg} rounded-xl flex items-center justify-center shadow`}>
            <m.icon className="text-white" size={16} />
          </div>
        ) : (
          <div className="w-9 h-9 bg-gray-100 rounded-xl flex items-center justify-center">
            <span className="text-sm font-bold text-gray-500">#{rank}</span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-800 text-sm truncate">{name}</p>
          {sub && <p className="text-xs text-gray-400 truncate">{sub}</p>}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">{label}</span>
        <span className="text-xl font-black" style={{ color }}>{count}</span>
      </div>
      <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(count * 10, 100)}%`, background: color }} />
      </div>
    </div>
  );
};

// ─── MAIN DASHBOARD ───────────────────────────────────────────────────────────
export default function Dashboard() {
  const [prData,   setPrData]   = useState([]);
  const [poData,   setPoData]   = useState([]);
  const [ratedPRs, setRatedPRs] = useState([]);
  const [ratedPOs, setRatedPOs] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  // Tab state
  const [activeTab, setActiveTab] = useState('overview');

  // Period filters
  const [prRatingFilter, setPrRatingFilter] = useState('month');
  const [poRatingFilter, setPoRatingFilter] = useState('month');
  const [bestPRFilter,   setBestPRFilter]   = useState('month');
  const [bestPOFilter,   setBestPOFilter]   = useState('month');

  const currentUser = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const isAdmin = currentUser?.positionId === 'SYSTEM ADMIN';

  // ─── FETCH ────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const uid = currentUser?.userId;

      const [prRes, poRes, ratedPRRes, ratedPORes] = await Promise.all([
        fetch(`${API_BASE_URL}api/PR_Header`),
        fetch(`${API_BASE_URL}api/T_PO_Header`),
        fetch(`${API_BASE_URL}api/PR_Header/rated`),
        fetch(`${API_BASE_URL}api/T_PO_Header/rated`),
      ]);

      if (prRes.ok) {
        const all = await prRes.json();
        setPrData(isAdmin ? all : all.filter(p => p.createdBy === uid));
      }
      if (poRes.ok) {
        const all = await poRes.json();
        setPoData(isAdmin ? all : all.filter(p => p.preparedBy === uid));
      }
      if (ratedPRRes.ok) {
        const all = await ratedPRRes.json();
        setRatedPRs(isAdmin ? all : all.filter(p => p.createdBy === uid));
      }
      if (ratedPORes.ok) {
        const all = await ratedPORes.json();
        setRatedPOs(isAdmin ? all : all.filter(p => p.preparedBy === uid));
      }

      setLastRefreshed(new Date());
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── COMPUTED STATS ───────────────────────────────────────────────────────
  const prStats = useMemo(() => ({
    total:    prData.length,
    draft:    prData.filter(p => p.status === 0).length,
    pending:  prData.filter(p => p.status === 1).length,
    approved: prData.filter(p => p.status === 2).length,
    rejected: prData.filter(p => p.status === 3).length,
  }), [prData]);

  const poStats = useMemo(() => ({
    total:    poData.length,
    draft:    poData.filter(p => p.status === 0).length,
    pending:  poData.filter(p => p.status === 1).length,
    approved: poData.filter(p => p.status === 2).length,
    rejected: poData.filter(p => p.status === 3).length,
  }), [poData]);

  // Top Requestors — PR
  const topPRRequestors = useMemo(() => {
    const map = {};
    prData.forEach(p => {
      const name = p.requestor || `User ${p.createdBy}`;
      const dept = p.department || p.departmentName || '—';
      if (!map[name]) map[name] = { name, dept, count: 0, approved: 0 };
      map[name].count++;
      if (p.status === 2) map[name].approved++;
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 10);
  }, [prData]);

  // Top PO Preparers
  const topPOPreparers = useMemo(() => {
    const map = {};
    poData.forEach(p => {
      const name = p.preparedByName || `User ${p.preparedBy}`;
      if (!map[name]) map[name] = { name, count: 0, approved: 0 };
      map[name].count++;
      if (p.status === 2) map[name].approved++;
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 10);
  }, [poData]);

  // Department breakdown
  const deptData = useMemo(() => {
    const map = {};
    prData.forEach(p => {
      const dept = p.department || p.departmentName || 'Unknown';
      if (!map[dept]) map[dept] = { dept, prs: 0, approved: 0, rejected: 0, pending: 0 };
      map[dept].prs++;
      if (p.status === 2) map[dept].approved++;
      if (p.status === 3) map[dept].rejected++;
      if (p.status === 1) map[dept].pending++;
    });
    return Object.values(map).sort((a, b) => b.prs - a.prs).slice(0, 10);
  }, [prData]);

  // Monthly data
  const monthlyData = useMemo(() => {
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return months.map((month, idx) => ({
      month,
      PR: prData.filter(p => new Date(p.creationDate).getMonth() === idx).length,
      PO: poData.filter(p => new Date(p.poCreatedDate).getMonth() === idx).length,
      Approved: prData.filter(p => new Date(p.creationDate).getMonth() === idx && p.status === 2).length,
    }));
  }, [prData, poData]);

  // 7-day activity
  const activityData = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(); d.setDate(d.getDate() - (6 - i));
      const ds = d.toISOString().slice(0, 10);
      return {
        day: d.toLocaleDateString('en-US', { weekday: 'short' }),
        PR: prData.filter(p => (p.creationDate || '').slice(0, 10) === ds).length,
        PO: poData.filter(p => (p.poCreatedDate || '').slice(0, 10) === ds).length,
      };
    });
  }, [prData, poData]);

  // 6-month trend
  const sixMonthData = useMemo(() => {
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(); d.setMonth(d.getMonth() - (5 - i));
      const m = d.getMonth(), y = d.getFullYear();
      return {
        month: d.toLocaleDateString('en-US', { month: 'short' }),
        PR: prData.filter(p => { const pd = new Date(p.creationDate); return pd.getMonth() === m && pd.getFullYear() === y; }).length,
        PO: poData.filter(p => { const pd = new Date(p.poCreatedDate); return pd.getMonth() === m && pd.getFullYear() === y; }).length,
      };
    });
  }, [prData, poData]);

  const prApprovalRate = prStats.total > 0 ? Math.round((prStats.approved / prStats.total) * 100) : 0;
  const poApprovalRate = poStats.total > 0 ? Math.round((poStats.approved / poStats.total) * 100) : 0;
  const rejectionRate  = (prStats.total + poStats.total) > 0
    ? Math.round(((prStats.rejected + poStats.rejected) / (prStats.total + poStats.total)) * 100) : 0;
  const conversionRate = prStats.approved > 0 ? Math.min(Math.round((poStats.total / prStats.approved) * 100), 999) : 0;

  const pieColors = ['#6B7280', '#F59E0B', '#10B981', '#EF4444'];

  // ─── TABS CONFIG ──────────────────────────────────────────────────────────
  const TABS = [
    { id: 'overview',    label: 'Overview',    Icon: LayoutDashboard },
    { id: 'analytics',   label: 'Analytics',   Icon: BarChart3        },
    { id: 'requestors',  label: 'Requestors',  Icon: Users            },
    { id: 'departments', label: 'Departments', Icon: Building2        },
    { id: 'best',        label: 'Best Orders', Icon: Trophy           },
    { id: 'rated',       label: 'Top Rated',   Icon: Star             },
  ];

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-sm text-gray-400 font-medium">Loading dashboard...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/50 to-indigo-50 p-6">
      <div className="max-w-screen-2xl mx-auto space-y-5">

        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl px-6 py-5 shadow-md border border-gray-100">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow">
                <Sparkles className="text-white" size={22} />
              </div>
              <div>
                <h1 className="text-2xl font-black bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Procurement Dashboard
                </h1>
                <p className="text-xs text-gray-400 mt-0.5">
                  Last refreshed: {lastRefreshed.toLocaleTimeString()} · {prStats.total + poStats.total} total documents
                </p>
              </div>
            </div>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl text-sm font-semibold transition-all border border-blue-100"
            >
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>

        {/* ── TAB BAR ────────────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-2">
          <div className="flex gap-1 flex-wrap">
            {TABS.map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  activeTab === id
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon size={15} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            TAB: OVERVIEW
        ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'overview' && (
          <div className="space-y-5">
            {/* Summary KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { label: 'Total Documents',   value: prStats.total + poStats.total,     gradient: 'from-blue-500 to-blue-600',    textColor: 'text-blue-700',    icon: Package,      sub: `${prStats.total} PR · ${poStats.total} PO` },
                { label: 'Awaiting Approval', value: prStats.pending + poStats.pending,  gradient: 'from-amber-500 to-orange-500', textColor: 'text-amber-700',   icon: Clock,        sub: 'Need attention' },
                { label: 'Approved',          value: prStats.approved + poStats.approved, gradient: 'from-emerald-500 to-green-500', textColor: 'text-emerald-700', icon: CheckCircle,  sub: 'Completed' },
                { label: 'Rejected',          value: prStats.rejected + poStats.rejected, gradient: 'from-rose-500 to-red-500',     textColor: 'text-rose-700',    icon: XCircle,      sub: 'Disapproved' },
              ].map((kpi, i) => (
                <KPICard key={i} {...kpi} />
              ))}
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* PR pie */}
              <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
                <div className="flex items-center gap-3 mb-5">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow">
                    <ClipboardList className="text-white" size={18} />
                  </div>
                  <h3 className="text-base font-bold text-gray-800">PR Status Breakdown</h3>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={STATUS_MAP && Object.entries(STATUS_MAP).map(([k, v]) => ({ name: v.label, value: prData.filter(p => p.status === parseInt(k)).length }))}
                      cx="50%" cy="50%" outerRadius={85} innerRadius={50} dataKey="value"
                      label={({ name, percent }) => percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : ''}
                      labelLine={false}
                    >
                      {pieColors.map((c, i) => <Cell key={i} fill={c} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-3 justify-center mt-2">
                  {Object.entries(STATUS_MAP).map(([k, v], i) => (
                    <span key={k} className="flex items-center gap-1.5 text-xs text-gray-500">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: pieColors[i] }} />
                      {v.label} ({prData.filter(p => p.status === parseInt(k)).length})
                    </span>
                  ))}
                </div>
              </div>

              {/* PO pie */}
              <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
                <div className="flex items-center gap-3 mb-5">
                  <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow">
                    <ShoppingCart className="text-white" size={18} />
                  </div>
                  <h3 className="text-base font-bold text-gray-800">PO Status Breakdown</h3>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={Object.entries(STATUS_MAP).map(([k, v]) => ({ name: v.label, value: poData.filter(p => p.status === parseInt(k)).length }))}
                      cx="50%" cy="50%" outerRadius={85} innerRadius={50} dataKey="value"
                      label={({ name, percent }) => percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : ''}
                      labelLine={false}
                    >
                      {pieColors.map((c, i) => <Cell key={i} fill={c} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-3 justify-center mt-2">
                  {Object.entries(STATUS_MAP).map(([k, v], i) => (
                    <span key={k} className="flex items-center gap-1.5 text-xs text-gray-500">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: pieColors[i] }} />
                      {v.label} ({poData.filter(p => p.status === parseInt(k)).length})
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Monthly bar */}
            <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl shadow">
                  <BarChart3 className="text-white" size={18} />
                </div>
                <h3 className="text-base font-bold text-gray-800">Monthly Volume — Full Year</h3>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={monthlyData} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" stroke="#9ca3af" style={{ fontSize: '11px' }} />
                  <YAxis stroke="#9ca3af" style={{ fontSize: '11px' }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="PR" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="PO" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="Approved" fill="#10b981" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            TAB: ANALYTICS
        ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'analytics' && (
          <div className="space-y-5">
            {/* KPI Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              <KPICard label="PR Approval Rate"   value={prApprovalRate}  suffix="%" icon={Target}       gradient="from-emerald-500 to-green-600"  textColor="text-emerald-600" sub={`${prStats.approved} of ${prStats.total} PRs`}  trend={prApprovalRate - 50} />
              <KPICard label="PO Approval Rate"   value={poApprovalRate}  suffix="%" icon={CheckCircle}  gradient="from-indigo-500 to-indigo-600"  textColor="text-indigo-600"  sub={`${poStats.approved} of ${poStats.total} POs`}  trend={poApprovalRate - 50} />
              <KPICard label="PR→PO Conversion"   value={conversionRate}  suffix="%" icon={ArrowUpRight} gradient="from-amber-500 to-orange-500"   textColor="text-amber-600"   sub={`${poStats.total} POs from ${prStats.approved} PRs`} />
              <KPICard label="Rejection Rate"     value={rejectionRate}   suffix="%" icon={XCircle}      gradient="from-rose-500 to-red-600"       textColor="text-rose-600"    sub={`${prStats.rejected + poStats.rejected} rejected total`} trend={-(rejectionRate)} />
            </div>

            {/* 7-day + 6-month */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
                <div className="flex items-center gap-3 mb-5">
                  <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl shadow">
                    <Zap className="text-white" size={18} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-800">7-Day Activity</h3>
                    <p className="text-xs text-gray-400">Submissions this week</p>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={activityData}>
                    <defs>
                      <linearGradient id="prG" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="poG" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="day" stroke="#9ca3af" style={{ fontSize: '11px' }} />
                    <YAxis stroke="#9ca3af" style={{ fontSize: '11px' }} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Area type="monotone" dataKey="PR" stroke="#3b82f6" strokeWidth={2.5} fill="url(#prG)" dot={{ r: 4, fill: '#3b82f6' }} />
                    <Area type="monotone" dataKey="PO" stroke="#8b5cf6" strokeWidth={2.5} fill="url(#poG)" dot={{ r: 4, fill: '#8b5cf6' }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
                <div className="flex items-center gap-3 mb-5">
                  <div className="p-2 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl shadow">
                    <TrendingUp className="text-white" size={18} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-800">6-Month Trend</h3>
                    <p className="text-xs text-gray-400">PR & PO volume over time</p>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={sixMonthData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" stroke="#9ca3af" style={{ fontSize: '11px' }} />
                    <YAxis stroke="#9ca3af" style={{ fontSize: '11px' }} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line type="monotone" dataKey="PR" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                    <Line type="monotone" dataKey="PO" stroke="#8b5cf6" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            TAB: REQUESTORS
        ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'requestors' && (
          <div className="space-y-5">
            {/* Top 3 medals */}
            {topPRRequestors.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wider mb-3">Top PR Requestors</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {topPRRequestors.slice(0, 3).map((r, i) => (
                    <RankCard
                      key={r.name}
                      rank={i + 1}
                      name={r.name}
                      count={r.count}
                      label="PRs submitted"
                      color={DEPT_COLORS[i]}
                      sub={r.dept}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* PR requestors full list */}
              <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
                <div className="flex items-center gap-3 mb-5">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow">
                    <ClipboardList className="text-white" size={18} />
                  </div>
                  <h3 className="text-base font-bold text-gray-800">All PR Requestors</h3>
                </div>
                {topPRRequestors.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-8">No data available</p>
                ) : (
                  <div className="space-y-1">
                    {topPRRequestors.map((r, i) => {
                      const max = topPRRequestors[0]?.count || 1;
                      const rate = r.count > 0 ? Math.round((r.approved / r.count) * 100) : 0;
                      return (
                        <div key={r.name} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 transition-colors">
                          <span className="text-xs font-bold text-gray-400 w-5 text-right">{i + 1}</span>
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                            style={{ background: DEPT_COLORS[i % DEPT_COLORS.length] }}>
                            {getInitials(r.name)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-semibold text-gray-800 truncate">{r.name}</span>
                              <span className="text-xs font-bold text-gray-700 ml-2">{r.count}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-700"
                                  style={{ width: `${Math.round((r.count / max) * 100)}%`, background: DEPT_COLORS[i % DEPT_COLORS.length] }}
                                />
                              </div>
                              <span className="text-xs text-gray-400 flex-shrink-0">{rate}% approved</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* PO preparers full list */}
              <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
                <div className="flex items-center gap-3 mb-5">
                  <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow">
                    <ShoppingCart className="text-white" size={18} />
                  </div>
                  <h3 className="text-base font-bold text-gray-800">All PO Preparers</h3>
                </div>
                {topPOPreparers.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-8">No data available</p>
                ) : (
                  <div className="space-y-1">
                    {topPOPreparers.map((r, i) => {
                      const max = topPOPreparers[0]?.count || 1;
                      const rate = r.count > 0 ? Math.round((r.approved / r.count) * 100) : 0;
                      return (
                        <div key={r.name} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 transition-colors">
                          <span className="text-xs font-bold text-gray-400 w-5 text-right">{i + 1}</span>
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                            style={{ background: DEPT_COLORS[(i + 4) % DEPT_COLORS.length] }}>
                            {getInitials(r.name)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-semibold text-gray-800 truncate">{r.name}</span>
                              <span className="text-xs font-bold text-gray-700 ml-2">{r.count}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-700"
                                  style={{ width: `${Math.round((r.count / max) * 100)}%`, background: DEPT_COLORS[(i + 4) % DEPT_COLORS.length] }}
                                />
                              </div>
                              <span className="text-xs text-gray-400 flex-shrink-0">{rate}% approved</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Combined bar chart */}
            <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl shadow">
                  <Users className="text-white" size={18} />
                </div>
                <h3 className="text-base font-bold text-gray-800">Top 8 Requestors — PR Volume</h3>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={topPRRequestors.slice(0, 8).map(r => ({ name: r.name.split(' ')[0], PRs: r.count, Approved: r.approved }))}
                  layout="vertical"
                  margin={{ left: 70, right: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" stroke="#9ca3af" style={{ fontSize: '11px' }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" stroke="#9ca3af" style={{ fontSize: '11px' }} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="PRs" fill="#6366f1" radius={[0, 4, 4, 0]} barSize={12} />
                  <Bar dataKey="Approved" fill="#10b981" radius={[0, 4, 4, 0]} barSize={12} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            TAB: DEPARTMENTS
        ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'departments' && (
          <div className="space-y-5">
            {/* Top 3 dept medals */}
            {deptData.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wider mb-3">Top Departments by PR Volume</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {deptData.slice(0, 3).map((d, i) => (
                    <RankCard
                      key={d.dept}
                      rank={i + 1}
                      name={d.dept}
                      count={d.prs}
                      label="total PRs"
                      color={DEPT_COLORS[i]}
                      sub={`${d.approved} approved · ${d.rejected} rejected`}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Dept bar */}
              <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
                <div className="flex items-center gap-3 mb-5">
                  <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl shadow">
                    <Building2 className="text-white" size={18} />
                  </div>
                  <h3 className="text-base font-bold text-gray-800">PRs by Department</h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={deptData.map((d, i) => ({ dept: d.dept, PRs: d.prs, Approved: d.approved, color: DEPT_COLORS[i] }))}
                    layout="vertical"
                    margin={{ left: 90, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis type="number" stroke="#9ca3af" style={{ fontSize: '10px' }} allowDecimals={false} />
                    <YAxis type="category" dataKey="dept" stroke="#9ca3af" style={{ fontSize: '10px' }} tickLine={false} width={90} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="PRs" fill="#6366f1" radius={[0, 4, 4, 0]} barSize={10} />
                    <Bar dataKey="Approved" fill="#10b981" radius={[0, 4, 4, 0]} barSize={10} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Dept approval rates */}
              <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
                <div className="flex items-center gap-3 mb-5">
                  <div className="p-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow">
                    <Target className="text-white" size={18} />
                  </div>
                  <h3 className="text-base font-bold text-gray-800">Department Approval Rate</h3>
                </div>
                <div className="space-y-3">
                  {deptData.map((d, i) => {
                    const rate = d.prs > 0 ? Math.round((d.approved / d.prs) * 100) : 0;
                    const color = rate >= 70 ? '#10b981' : rate >= 40 ? '#f59e0b' : '#ef4444';
                    return (
                      <div key={d.dept}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: DEPT_COLORS[i % DEPT_COLORS.length] }} />
                            <span className="text-sm font-semibold text-gray-700 truncate max-w-[160px]">{d.dept}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">{d.prs} PRs</span>
                            <span className="text-xs font-bold" style={{ color }}>{rate}%</span>
                          </div>
                        </div>
                        <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${rate}%`, background: color }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Full dept table */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow">
                  <Activity className="text-white" size={18} />
                </div>
                <h3 className="text-base font-bold text-gray-800">Full Department Table</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {['#','Department','Total PRs','Approved','Pending','Rejected','Approval Rate'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {deptData.map((d, i) => {
                      const rate = d.prs > 0 ? Math.round((d.approved / d.prs) * 100) : 0;
                      const rateCls = rate >= 70 ? 'bg-green-100 text-green-800' : rate >= 40 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800';
                      return (
                        <tr key={d.dept} className="hover:bg-blue-50/30 transition-colors">
                          <td className="px-4 py-3 text-xs text-gray-400 font-bold">{i + 1}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: DEPT_COLORS[i % DEPT_COLORS.length] }} />
                              <span className="text-sm font-semibold text-gray-800">{d.dept}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-gray-800">{d.prs}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-emerald-600">{d.approved}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-amber-600">{d.pending}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-red-500">{d.rejected}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${rateCls}`}>
                              {rate}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            TAB: BEST ORDERS
        ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'best' && (
          <div className="space-y-5">
            {/* PR Best */}
            <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow">
                    <ClipboardList className="text-white" size={18} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-800">Best Approved PRs</h3>
                    <p className="text-xs text-gray-400">Most recent approved purchase requisitions</p>
                  </div>
                </div>
                <TimeFilterBtns value={bestPRFilter} onChange={setBestPRFilter} options={PERIOD_OPTS} />
              </div>

              {(() => {
                const items = filterByPeriod(prData, bestPRFilter, 'creationDate')
                  .filter(p => p.status === 2)
                  .sort((a, b) => new Date(b.creationDate) - new Date(a.creationDate))
                  .slice(0, 6);
                const gradients = [
                  'from-blue-500 to-blue-600', 'from-indigo-500 to-indigo-600',
                  'from-violet-500 to-violet-600', 'from-cyan-500 to-cyan-600',
                  'from-teal-500 to-teal-600', 'from-sky-500 to-sky-600',
                ];
                return items.length === 0 ? (
                  <div className="text-center py-10 text-gray-400">
                    <ClipboardList size={40} className="mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No approved PRs found for this period</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((pr, i) => (
                      <div key={pr.id} className="rounded-xl border border-gray-100 p-4 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5">
                        <div className="flex items-center justify-between mb-3">
                          <div className={`w-9 h-9 bg-gradient-to-br ${gradients[i]} rounded-lg flex items-center justify-center shadow`}>
                            <ShoppingBag className="text-white" size={16} />
                          </div>
                          <StatusBadge status={pr.status} />
                        </div>
                        <p className="font-black text-gray-800 text-sm mb-1">{pr.prNo || '—'}</p>
                        <p className="text-xs text-gray-500 mb-1">{pr.requestor || '—'}</p>
                        <p className="text-xs text-gray-400 mb-2">{pr.department || '—'}</p>
                        <p className="text-xs text-gray-400 line-clamp-2 mb-3">{pr.remarks || 'No remarks'}</p>
                        <div className="flex items-center justify-between text-xs text-gray-400">
                          <span>{fmtDate(pr.creationDate)}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${pr.priority === 'Emergency Order' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                            {pr.priority || 'Normal'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* PO Best */}
            <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow">
                    <ShoppingCart className="text-white" size={18} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-800">Best Approved POs</h3>
                    <p className="text-xs text-gray-400">Most recent approved purchase orders</p>
                  </div>
                </div>
                <TimeFilterBtns value={bestPOFilter} onChange={setBestPOFilter} options={PERIOD_OPTS} />
              </div>

              {(() => {
                const items = filterByPeriod(poData, bestPOFilter, 'poCreatedDate')
                  .filter(p => p.status === 2)
                  .sort((a, b) => new Date(b.poCreatedDate) - new Date(a.poCreatedDate))
                  .slice(0, 6);
                const gradients = [
                  'from-purple-500 to-purple-600', 'from-pink-500 to-pink-600',
                  'from-rose-500 to-rose-600', 'from-fuchsia-500 to-fuchsia-600',
                  'from-violet-500 to-violet-600', 'from-indigo-500 to-indigo-600',
                ];
                return items.length === 0 ? (
                  <div className="text-center py-10 text-gray-400">
                    <ShoppingCart size={40} className="mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No approved POs found for this period</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((po, i) => (
                      <div key={po.id} className="rounded-xl border border-gray-100 p-4 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5">
                        <div className="flex items-center justify-between mb-3">
                          <div className={`w-9 h-9 bg-gradient-to-br ${gradients[i]} rounded-lg flex items-center justify-center shadow`}>
                            <ShoppingCart className="text-white" size={16} />
                          </div>
                          <StatusBadge status={po.status} />
                        </div>
                        <p className="font-black text-gray-800 text-sm mb-1">{po.poNumber || '—'}</p>
                        <p className="text-xs text-gray-500 mb-1">{po.shipTo_Location || '—'}</p>
                        <p className="text-xs text-gray-400 mb-2">{po.shipTo_ContactPerson || '—'}</p>
                        <p className="text-xs text-gray-400 line-clamp-2 mb-3">{po.remarks || 'No remarks'}</p>
                        <div className="flex items-center justify-between text-xs text-gray-400">
                          <span>{fmtDate(po.poCreatedDate)}</span>
                          <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold">
                            {po.paymentTerms || 'N/A'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            TAB: TOP RATED
        ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'rated' && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Top Rated PRs */}
              <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow">
                      <ClipboardList className="text-white" size={18} />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-800">Top 3 Rated PRs</h3>
                      <p className="text-xs text-gray-400">{ratedPRs.length} rated PRs total</p>
                    </div>
                  </div>
                  <TimeFilterBtns value={prRatingFilter} onChange={setPrRatingFilter} options={PERIOD_OPTS} />
                </div>

                {(() => {
                  const items = filterByPeriod(ratedPRs, prRatingFilter, 'creationDate')
                    .sort((a, b) => (b.rate || 0) - (a.rate || 0))
                    .slice(0, 3);
                  const styles = [
                    { gradient: 'from-blue-500 to-blue-600', border: 'border-blue-200', bg: 'bg-blue-50', text: 'text-blue-700' },
                    { gradient: 'from-indigo-500 to-indigo-600', border: 'border-indigo-200', bg: 'bg-indigo-50', text: 'text-indigo-700' },
                    { gradient: 'from-violet-500 to-violet-600', border: 'border-violet-200', bg: 'bg-violet-50', text: 'text-violet-700' },
                  ];
                  return items.length === 0 ? (
                    <div className="text-center py-10">
                      <Star size={40} className="mx-auto mb-3 text-gray-200" />
                      <p className="text-sm text-gray-400">No rated PRs for this period</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4">
                      {items.map((pr, i) => {
                        const s = styles[i];
                        return (
                          <div key={pr.id} className={`rounded-xl border-2 ${s.border} p-4 hover:shadow-lg transition-all duration-300`}>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 bg-gradient-to-br ${s.gradient} rounded-lg flex items-center justify-center shadow`}>
                                  <span className="text-white text-xs font-black">#{i + 1}</span>
                                </div>
                                <span className={`${s.bg} ${s.text} text-xs font-bold px-2 py-0.5 rounded-md`}>RANK #{i + 1}</span>
                              </div>
                              <Stars rating={pr.rate} />
                            </div>
                            <p className="font-black text-gray-800 text-sm mb-1">{pr.prNo || '—'}</p>
                            <p className="text-xs text-gray-500 mb-1">{pr.requestor || '—'}</p>
                            <p className="text-xs text-gray-400 line-clamp-1 mb-3">{pr.remarks || 'No remarks'}</p>
                            <div className={`bg-gradient-to-r ${s.gradient} rounded-lg p-2.5 flex items-center justify-between`}>
                              <span className="text-white/80 text-xs font-semibold">Rating score</span>
                              <span className="text-white text-xl font-black">{(pr.rate || 0).toFixed(1)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Top Rated POs */}
              <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow">
                      <ShoppingCart className="text-white" size={18} />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-800">Top 3 Rated POs</h3>
                      <p className="text-xs text-gray-400">{ratedPOs.length} rated POs total</p>
                    </div>
                  </div>
                  <TimeFilterBtns value={poRatingFilter} onChange={setPoRatingFilter} options={PERIOD_OPTS} />
                </div>

                {(() => {
                  const items = filterByPeriod(ratedPOs, poRatingFilter, 'poCreatedDate')
                    .sort((a, b) => (b.rate || 0) - (a.rate || 0))
                    .slice(0, 3);
                  const styles = [
                    { gradient: 'from-purple-500 to-purple-600', border: 'border-purple-200', bg: 'bg-purple-50', text: 'text-purple-700' },
                    { gradient: 'from-pink-500 to-pink-600', border: 'border-pink-200', bg: 'bg-pink-50', text: 'text-pink-700' },
                    { gradient: 'from-rose-500 to-rose-600', border: 'border-rose-200', bg: 'bg-rose-50', text: 'text-rose-700' },
                  ];
                  return items.length === 0 ? (
                    <div className="text-center py-10">
                      <Star size={40} className="mx-auto mb-3 text-gray-200" />
                      <p className="text-sm text-gray-400">No rated POs for this period</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4">
                      {items.map((po, i) => {
                        const s = styles[i];
                        return (
                          <div key={po.id} className={`rounded-xl border-2 ${s.border} p-4 hover:shadow-lg transition-all duration-300`}>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 bg-gradient-to-br ${s.gradient} rounded-lg flex items-center justify-center shadow`}>
                                  <span className="text-white text-xs font-black">#{i + 1}</span>
                                </div>
                                <span className={`${s.bg} ${s.text} text-xs font-bold px-2 py-0.5 rounded-md`}>RANK #{i + 1}</span>
                              </div>
                              <Stars rating={po.rate} />
                            </div>
                            <p className="font-black text-gray-800 text-sm mb-1">{po.poNumber || '—'}</p>
                            <p className="text-xs text-gray-500 mb-1">{po.shipTo_Location || '—'}</p>
                            <p className="text-xs text-gray-400 line-clamp-1 mb-3">{po.remarks || 'No remarks'}</p>
                            <div className={`bg-gradient-to-r ${s.gradient} rounded-lg p-2.5 flex items-center justify-between`}>
                              <span className="text-white/80 text-xs font-semibold">Rating score</span>
                              <span className="text-white text-xl font-black">{(po.rate || 0).toFixed(1)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}